<script setup lang="ts">
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import * as z from "zod"

import { Button } from "@/components/ui/button"
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import DetailContainer from "@/views/layouts/components/DetailContainer.vue"
import { useRouter } from "vue-router"

const router = useRouter()

const formSchema = toTypedSchema(
  z.object({
    search_term: z.string().optional(),
    exact_match: z.boolean().optional(),
    author: z.string().optional(),
    language: z.string().optional(),
    number: z.string().optional(),
    from: z
      .string()
      .optional()
      .refine((value) => !value || (parseInt(value) >= 1970 && parseInt(value) <= new Date().getFullYear()), {
        message: "Inserire un anno compreso tra il 1970 e l'anno corrente"
      }),
    to: z
      .string()
      .optional()
      .refine((value) => !value || (parseInt(value) >= 1970 && parseInt(value) <= new Date().getFullYear()), {
        message: "Inserire un anno compreso tra il 1970 e l'anno corrente"
      })
  })
)

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    exact_match: false,
    language: "all",
    number: "all",
    from: undefined,
    to: undefined
  }
})

const handleChangeCheckbox = (value: boolean) => {
  form.setFieldValue("exact_match", value)
}

const onReset = () => {
  setTimeout(() => {
    form.resetForm({
      values: {
        exact_match: false,
        language: "all",
        number: "all",
        from: undefined,
        to: undefined
      }
    })
  }, 200)
}

const onSubmit = form.handleSubmit((values) => {
  // Convert booleans to string for query params
  router.push({
    name: "advanced_search_results_path",
    query: {
      ...values,
      exact_match: values.exact_match ? "true" : "false"
    }
  })
})
</script>

<template>
  <DetailContainer class="container flex flex-col">
    <div class="title">{{ $t("advanced_search.title") }}</div>
    <form @submit="onSubmit">
      <div class="flex flex-col gap-8">
        <div class="flex gap-4">
          <div class="flex-1">
            <FormField v-slot="{ componentField }" name="search_term">
              <FormItem>
                <FormLabel>{{ $t("advanced_search.keyword") }}</FormLabel>

                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>

                <FormDescription />

                <FormMessage />
              </FormItem>
            </FormField>
          </div>
<!--          <div class="flex-0 relative top-3 flex items-center gap-2">-->
<!--            <FormField name="exact_match">-->
<!--              <FormItem>-->
<!--                <FormLabel />-->

<!--                <FormControl>-->
<!--                  <Checkbox :checked="form.values.exact_match" @update:checked="handleChangeCheckbox" />-->

<!--                  <FormLabel-->
<!--                    class="relative bottom-0.5 ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">-->
<!--                    {{ $t("advanced_search.exact_match") }}-->
<!--                  </FormLabel>-->
<!--                </FormControl>-->

<!--                <FormDescription />-->

<!--                <FormMessage />-->
<!--              </FormItem>-->
<!--            </FormField>-->
<!--          </div>-->
        </div>

        <div class="flex gap-4">
          <div class="flex-1">
            <FormField v-slot="{ componentField }" name="author">
              <FormItem>
                <FormLabel>{{ $t("advanced_search.author") }}</FormLabel>

                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>

                <FormDescription />

                <FormMessage />
              </FormItem>
            </FormField>
          </div>

          <div class="flex-1">
            <FormField v-slot="{ componentField }" name="language">
              <FormItem>
                <FormLabel>{{ $t("advanced_search.language") }}</FormLabel>

                <Select v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">{{ $t("advanced_search.languages.all") }}</SelectItem>
                      <SelectItem value="it">{{ $t("commons.localized_languages.it") }}</SelectItem>
                      <SelectItem value="en">{{ $t("commons.localized_languages.en") }}</SelectItem>
                      <SelectItem value="de">{{ $t("commons.localized_languages.de") }}</SelectItem>
                      <SelectItem value="fr">{{ $t("commons.localized_languages.fr") }}</SelectItem>
                      <SelectItem value="es">{{ $t("commons.localized_languages.es") }}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <FormDescription />

                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>

        <div class="flex gap-4">
          <div class="flex flex-1 gap-2">
            <div class="flex-1">
              <FormField v-slot="{ componentField }" name="from">
                <FormItem>
                  <FormLabel>{{ $t("commons.year") }} ( {{ $t('advanced_search.publication_date_from') }} )</FormLabel>
                  <FormControl>
                    <Input v-bind="componentField" />
                  </FormControl>

                  <FormDescription />

                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <div class="flex-1">
              <FormField v-slot="{ componentField }" name="to">
                <FormItem>
                  <FormLabel>{{ $t("commons.year") }} ({{ $t('advanced_search.publication_date_to') }}) </FormLabel>
                  <FormControl>
                    <Input v-bind="componentField" />
                  </FormControl>

                  <FormDescription />

                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
          </div>

          <div class="flex-1">
            <FormField v-slot="{ componentField }" name="number">
              <FormItem>
                <FormLabel>{{ $t("commons.number") }}</FormLabel>

                <Select v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">{{ $t("advanced_search.numbers.all") }}</SelectItem>
                      <SelectItem value="1"> 1</SelectItem>
                      <SelectItem value="2"> 2</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <FormDescription />

                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>

      <div class="mt-12 flex items-center justify-end gap-4">
        <Button variant="secondary" type="button" @click.prevent="onReset">{{ $t("advanced_search.reset") }}</Button>
        <Button type="submit">{{ $t("advanced_search.search") }}</Button>
      </div>
    </form>
  </DetailContainer>
</template>
